// "Motive implicite": caiet UC50 step 2 (NC-UC50-03), "modifică motivele implicite
// pentru mișcări de stoc noi din cele asociate tipului Registratorului și direcția
// modificării". The reason a new movement takes in each direction, offered from the
// active Registrar's type's row of the reason table only (UC50-15, CF-15), and the
// direction a new movement starts from before its level is typed
// (docs/shape-ops-default-reasons.md §Decision 4). Corectare is no direction's
// default, so neither list offers it.
import { useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { MudButton, MudModal, MudSegmentedControl, MudSelect } from '@egov-moldova/mud-react';
import {
  REASONS_BY_REGISTRAR_KIND,
  type MovementDirection,
  type RegistrarKind,
  type StockMovementReason,
} from '@simsm/shared-types';
import { onModalDismiss } from '@simsm/ui-shared';
import { reasonLabelKey, type DefaultReasons } from '../../../features/reporting/movement-form';

/** What "Aplică" hands to the page: both reasons and the direction, always all three. */
export interface DefaultReasonsChoice extends DefaultReasons {
  direction: MovementDirection;
}

export interface DefaultReasonsDialogProps {
  open: boolean;
  /** The defaults in force: the stored ones the type allows, else the type's starting ones. */
  defaults: DefaultReasons;
  direction: MovementDirection;
  /** The active Registrar's type, whose reasons the two lists hold. */
  registrarKind: RegistrarKind;
  onApply: (next: DefaultReasonsChoice) => void;
  onClose: () => void;
}

function reasonIn(
  reasons: ReadonlyArray<StockMovementReason>,
  value: string,
): StockMovementReason | undefined {
  return reasons.find((reason) => reason === value);
}

export function DefaultReasonsDialog({
  open,
  defaults,
  direction,
  registrarKind,
  onApply,
  onClose,
}: DefaultReasonsDialogProps): JSX.Element {
  const { t } = useTranslate();
  const [increase, setIncrease] = useState(defaults.increase);
  const [decrease, setDecrease] = useState(defaults.decrease);
  const [startDirection, setStartDirection] = useState(direction);
  const table = REASONS_BY_REGISTRAR_KIND[registrarKind];
  const optionsOf = (reasons: ReadonlyArray<StockMovementReason>) =>
    reasons.map((reason) => ({ value: reason, label: t(reasonLabelKey(reason)) }));

  return (
    <MudModal
      closeLabel={t('common.close')}
      open={open}
      titleText={t('stock_reporting.defaults.title')}
      size="md"
      // AGE-BUG(mudClose-bubbles): a select closing its list inside the modal
      // reaches this handler too; the guard answers only a real dismissal.
      onMudClose={onModalDismiss(onClose)}
    >
      <div className="simsm-stock-reporting__confirm">
        <p className="simsm-stock-reporting__confirm-meta">
          {t('stock_reporting.defaults.body', { kind: t(`registrars.kind.${registrarKind}`) })}
        </p>
        <div className="simsm-default-reasons__field">
          <span className="simsm-form-page__field-label">
            {t('stock_reporting.defaults.direction.label')}
          </span>
          <MudSegmentedControl
            size="md"
            value={startDirection}
            aria-label={t('stock_reporting.defaults.direction.label')}
            segments={[
              { value: 'increase', label: t('stock_reporting.defaults.direction.increase') },
              { value: 'decrease', label: t('stock_reporting.defaults.direction.decrease') },
            ]}
            onMudChange={(e: CustomEvent<{ value: string }>) =>
              setStartDirection(e.detail.value === 'increase' ? 'increase' : 'decrease')
            }
          />
          <span className="simsm-default-reasons__hint">
            {t('stock_reporting.defaults.direction.hint')}
          </span>
        </div>
        <MudSelect
          size="medium"
          required
          label={t('stock_reporting.defaults.increase.label')}
          value={increase}
          onMudChange={(e: CustomEvent<{ value: string }>) => {
            const next = reasonIn(table.increase, e.detail.value);
            if (next) setIncrease(next);
          }}
        >
          {optionsOf(table.increase).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </MudSelect>
        <MudSelect
          size="medium"
          required
          label={t('stock_reporting.defaults.decrease.label')}
          value={decrease}
          onMudChange={(e: CustomEvent<{ value: string }>) => {
            const next = reasonIn(table.decrease, e.detail.value);
            if (next) setDecrease(next);
          }}
        >
          {optionsOf(table.decrease).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </MudSelect>
      </div>
      <div slot="actions" className="simsm-stock-reporting__confirm-actions">
        <MudButton shape="circular" appearance="text" variant="strict" size="md" onClick={onClose}>
          {t('stock_reporting.defaults.action.cancel')}
        </MudButton>
        <MudButton
          shape="circular"
          variant="primary"
          size="md"
          onClick={() => onApply({ increase, decrease, direction: startDirection })}
        >
          {t('stock_reporting.defaults.action.apply')}
        </MudButton>
      </div>
    </MudModal>
  );
}
